import scrollTo from './utils';

const dataAttributeName = 'data-ua-go-to-options';
const defaults = {
  pageContainerSelector: 'html, body',
  targetSelector: null,
  compensationSelector: null,
  animationInit: 'u-animated',
  animationIn: 'u-a-fade-in-up',
  animationOut: 'u-a-fade-out-down',
  duration: 800,
  offsetTop: 0,
  position: {
    init: null,
    hide: null,
    show: null,
  },
  isReferencedToOtherPage: null,
  preventEventClass: 'ua-go-to-prevent-event',
};

export default class UaGoTo {
  /**
   * Initializes an instance of the UaGoTo class.
   * @param {HTMLElement|NodeList|Object} el - Element(s) to apply scroll behavior.
   * @param {Object} [options={}] - Configuration options for scroll behavior.
   * @param {string} [id] - Optional identifier for the element.
   */
  constructor(el, options = {}, id) {
    this.collection = [];
    const elements = el instanceof HTMLElement ? [el] : el instanceof Object ? el : document.querySelectorAll(el);

    elements.forEach((element) => {
      this.addToCollection(element, options, id || element.id);
    });

    if (this.collection.length) {
      this._init();
    }
  }

  /**
   * Initializes each element in the collection by setting properties,
   * styles, and attaching event listeners.
   * @private
   */
  _init() {
    this.collection.forEach((item) => {
      if (item.$initializedEl) return;

      const { $el, options } = item;
      const compensationEl = document.querySelector(options.compensationSelector);
      const targetEl = document.querySelector(options.targetSelector);
      const pageContainerEl = document.querySelector(options.pageContainerSelector);

      options.targetOffsetTop = () =>
        targetEl ? (compensationEl ? targetEl.offsetTop - compensationEl.clientHeight : targetEl.offsetTop) : 0;

      this.prepareObject($el, options);

      if (options.position) {
        this.setPosition($el, options.position.init);
      }

      $el.addEventListener('click', (event) => this.clickEvents(event, $el, options, pageContainerEl));

      if (options.animationIn && options.animationOut) {
        document.addEventListener('scroll', () => this.scrollEvents($el, options));
      }
    });
  }

  /**
   * Adds classes and settings to prepare the element for animations.
   * @param {HTMLElement} $el - The target element.
   * @param {Object} settings - Configuration for animation and styles.
   */
  prepareObject($el, settings) {
    if (settings.animationIn && settings.animationOut) {
      if (/MSIE 10.0/.test(navigator.userAgent)) {
        document.documentElement.classList.add('ie10');
      }
      $el.classList.add(settings.animationInit, settings.animationOut, settings.preventEventClass);
    }
  }

  /**
   * Sets the initial or dynamic position styles on the element.
   * @param {HTMLElement} $el - The target element.
   * @param {Object} styles - CSS style properties to apply.
   */
  setPosition($el, styles = {}) {
    Object.entries(styles).forEach(([property, value]) => {
      $el.style.setProperty(property, value);
    });
  }

  /**
   * Handles click events for smooth scroll behavior.
   * @param {Event} event - Click event.
   * @param {HTMLElement} $el - The target element.
   * @param {Object} settings - Scroll configuration.
   * @param {HTMLElement} _pageContainerSelector - The container to scroll within.
   */
  clickEvents(event, $el, settings, pageContainerEl) {
    if (!settings.isReferencedToOtherPage) {
      event.preventDefault();
      scrollTo({ to: settings.targetOffsetTop(), el: pageContainerEl }, settings.duration);
    }
  }

  /**
   * Manages scroll-based visibility and animation of the element.
   * @param {HTMLElement} $el - The target element.
   * @param {Object} settings - Animation configuration based on scroll.
   */
  scrollEvents($el, settings) {
    const { offsetTop, position, animationIn, animationOut } = settings;
    $el.style.visibility = '';

    if (window.scrollY >= offsetTop) {
      this.setPosition($el, position.show);
      $el.classList.remove(animationOut);
      $el.classList.add(animationIn);
    } else {
      this.setPosition($el, position.hide);
      $el.classList.remove(animationIn);
      $el.classList.add(animationOut);
    }
  }

  /**
   * Adds an element to the collection with applied configuration settings.
   * @param {HTMLElement} item - Element to add to the scroll collection.
   * @param {Object} options - Configuration options for the element.
   * @param {string} id - Unique identifier for the element.
   */
  addToCollection(item, options, id) {
    this.collection.push({
      $el: item,
      id: id || null,
      options: {
        ...defaults,
        ...(item.hasAttribute(dataAttributeName) ? JSON.parse(item.getAttribute(dataAttributeName)) : {}),
        ...options,
      },
    });
  }

  /**
   * Retrieves an element by ID or index from the collection.
   * @param {string|number} item - Index or ID of the element to retrieve.
   * @returns {HTMLElement|undefined} - The matched element or undefined if not found.
   */
  getItem(item) {
    if (typeof item === 'number') {
      return this.collection[item]?.$initializedEl;
    } else {
      return this.collection.find((el) => el.id === item)?.$initializedEl;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Ensure UaGoTo is defined and imported correctly
  if (typeof UaGoTo === 'undefined') {
    console.error('UaGoTo is not defined.');
  } else {
    // Initialize the .js-go-to elements
    try {
      const elements = document.querySelectorAll('.js-go-to');
      elements.forEach((el) => {
        new UaGoTo(el);
      });
    } catch (error) {
      console.error('Error initializing UaGoTo:', error);
    }
  }
});
